import { useEffect, useState } from "react";
import Logo from "../../assets/images/stratagenIcon.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { Loader2, CheckCircle } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { TruncateWithTooltip } from "./tooltip";
import {
  createModifiedDicomBlob,
  handleGroupBySeries,
  handleToValidateQualifierPhantom,
  loadDicomFile,
  organizeMetadata,
  processZipFileWithSeparation,
  isSupportedOtherFile,
  isNumericField,
  getDropAreaMessage,
} from "./utils";
import {
  fetchValidatorJson,
  uploadPortal,
  uploadSingleOtherFile,
  publishToPubSub,
} from "../../utils";
import SuccessPopup from "./sucessPopup";
import StudyInfoTable from "./studyInfoTable";
import {
  AlertProps,
  SecondaryStudyData,
  SecondaryStudySelection,
  StudyFields,
} from "@/types/types";
import StudyFieldsComponent from "./studyFields";

function UploadPortal() {
  const [hierarchicalMetadata, setHierarchicalMetadata] = useState<any | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [currentDicomFiles, setCurrentDicomFiles] = useState<any[]>([]);
  const [studyFields, setStudyFields] = useState<StudyFields | null>(null);
  const [openRows, setOpenRows] = useState<any>({});
  const [seriesDataToShow, setSeriesDataToShow] = useState<any>([]);
  const [selectedFileName, setSelectedFileName] = useState<any>([]);
  const [submitButtonLoading, setSubmitButtonLoading] =
    useState<boolean>(false);
  const [validatorJson, setValidatorJson] = useState<any>(null);
  const { toast } = useToast();
  const { logout } = useAuth0();
  const [openAlert, setOpenAlert] = useState<AlertProps | null>(null);
  const [openGroupedSeriesRow, setOpenGroupedSeriesRow] = useState<any>({});
  const { secondaryStudySelection, pubsub } = (window as any).data;
  const [studyData, setStudyData] = useState<any>([]);
  const [customTagData, setCustomTagData] = useState<any>({});
  const [fileType, setFileType] = useState<"dicom" | "other" | "mixed" | null>(
    null
  );
  const [currentOtherFiles, setCurrentOtherFiles] = useState<File[]>([]);
  const [hasFiles, setHasFiles] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    dicom: false,
    other: false,
  });

  useEffect(() => {
    if (error.length > 0) {
      toast({
        variant: "destructive",
        title: error,
        duration: 30000,
      });
    }
  }, [error]);

  useEffect(() => {
    const getValidatorJson = async () => {
      const validatorJson = await fetchValidatorJson();
      setValidatorJson(validatorJson);
    };
    setStudyData(secondaryStudySelection);
    getValidatorJson();
  }, [secondaryStudySelection]);
  useEffect(() => {
    console.log({
      submitButtonLoading,
      shouldShowValidation: fileType === "dicom" || fileType === "mixed",
      hierarchicalMetadataStudiesLength: hierarchicalMetadata?.studies?.length,
      shouldShowOtherFiles: fileType === "other" || fileType === "mixed",
      currentOtherFilesLength: currentOtherFiles.length,
      uploadedFiles,
    });
  }, [
    submitButtonLoading,
    fileType,
    hierarchicalMetadata,
    currentOtherFiles,
    uploadedFiles,
  ]);

  const toggleRow = (id: any) => {
    setOpenRows((prev: any) => {
      const isCurrentlyOpen = prev[id];
      return {
        ...prev,
        [id]: !isCurrentlyOpen,
      };
    });
  };

  const handleOpenRow = (id: any) => {
    setOpenGroupedSeriesRow((prev: any) => {
      const isCurrentlyOpen = prev[id];
      if (isCurrentlyOpen) {
        setOpenRows({});
      }
      return {
        ...prev,
        [id]: !isCurrentlyOpen,
      };
    });
  };

  const getHumanSubjectId = (
    studyFields: StudyFields | null,
    customTagData: { [key: string]: any }
  ): string | null => {
    if (!studyFields || !customTagData[studyFields.id]) {
      return null;
    }
    
    const isHumanSubjectStudy =
      studyFields.studyCondition === "Human Subject MRI Scan" ||
      studyFields.studyCondition === "Human Subject CRF or Study Material";
    
    if (isHumanSubjectStudy) {
      return customTagData[studyFields.id]["HumanSubjectId"] || null;
    }
    
    return null;
  };

  const createFileWithPrefix = (originalFile: File, prefix: string): File => {
  const newFileName = `${prefix}_${originalFile.name}`;
  return new File([originalFile], newFileName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setSubmitButtonLoading(true);
    const { pubsubEvents } = pubsub;
    const authToken = localStorage.getItem("accessToken");
    if (!authToken) {
      toast({
        variant: "destructive",
        title: "Authentication token not found",
        duration: 30000,
      });
      setIsLoading(false);
      setSubmitButtonLoading(false);
      return;
    }
    let otherFilesSuccess = false;
    let dicomFilesSuccess = false;

    const humanSubjectId = getHumanSubjectId(studyFields, customTagData);

    try {
      if (currentDicomFiles.length > 0 && !uploadedFiles.dicom) {
        if (!studyFields) {
          toast({
            variant: "destructive",
            title: "Please select a Stratagen Study ID.",
            duration: 30000,
          });
          setIsLoading(false);
          setSubmitButtonLoading(false);
          return;
        }

        if (studyFields.fields && studyFields.fields.length > 0) {
          const currentStudySelections = customTagData[studyFields.id] || {};
          const allFieldsSelected = studyFields.fields.every(
            (field: SecondaryStudyData) => {
              const fieldValue = currentStudySelections[field.id];
              if (field.component === "dropdown") {
                return (
                  fieldValue !== undefined &&
                  fieldValue !== null &&
                  fieldValue !== ""
                );
              } else if (field.component === "input") {
                if (
                  !fieldValue ||
                  fieldValue === "" ||
                  fieldValue.trim() === ""
                ) {
                  return false;
                }
                if (isNumericField(field.id)) {
                  let cleanValue = fieldValue.replace(/[°F%]/g, "");
                  return (
                    cleanValue.toLowerCase() === "n/a" ||
                    /^-?\d+(\.\d{0,3})?$/.test(cleanValue)
                  );
                } else {
                  return fieldValue.trim() !== "";
                }
              }
              return true;
            }
          );

          if (!allFieldsSelected) {
            toast({
              variant: "destructive",
              title: "Please fill all required options correctly.",
              duration: 30000,
            });
            setIsLoading(false);
            setSubmitButtonLoading(false);
            return;
          }
        }

        let customTagValue: any = null;
        if (studyFields?.fields?.length > 0) {
          customTagValue = { studyCondition: studyFields.studyCondition };
          studyFields.fields.forEach((field) => {
            const fieldValue = customTagData[studyFields.id]?.[field.id];
            if (fieldValue) {
              customTagValue[field.id] = fieldValue;
            }
          });
          if (Object.keys(customTagValue).length === 1) {
            customTagValue = null;
          }
        } else {
          customTagValue = studyFields?.studyCondition
            ? { studyCondition: studyFields.studyCondition }
            : null;
        }

        const userEmail = localStorage.getItem("userEmail");
        const uploadTime = new Date().toISOString();
        const customTagsValue = {
          ...customTagValue,
          UploadedBy: userEmail || undefined,
          UploadDateTime: uploadTime,
        };
        const customTags = customTagValue
          ? [
              {
                tag: "x2F470010",
                vr: "LO",
                value: JSON.stringify(customTagsValue),
              },
            ]
          : [];
        try {
          const boundary =
            "----WebKitFormBoundary" + Math.random().toString(36).substring(2);
          const files = [];
          for (let i = 0; i < currentDicomFiles.length; i++) {
            const originalBuffer =
              currentDicomFiles[i].dataSet.byteArray.buffer;
            const modifiedBlob = createModifiedDicomBlob(
              originalBuffer,
              customTags as any
            );
            files.push(modifiedBlob);
          }
          const parts = [];
          files.forEach((file, index) => {
            parts.push(`--${boundary}\r\n`);
            parts.push("Content-Type: application/dicom\r\n");
            parts.push(`Content-Length: ${file.size}\r\n`);
            parts.push("\r\n");
            parts.push(file);
            parts.push("\r\n");
          });
          parts.push(`--${boundary}--\r\n`);
          const finalBlob = new Blob(parts, {
            type: `multipart/related; type="application/dicom"; boundary=${boundary}`,
          });

          // Publish ingestStarted for each study
          if (!hierarchicalMetadata?.studies?.length) {
            toast({
              variant: "destructive",
              title: "No valid studies found for upload",
              duration: 30000,
            });
            setIsLoading(false);
            setSubmitButtonLoading(false);
            return;
          }

          for (const study of hierarchicalMetadata.studies) {
            try {
              await publishToPubSub(
                pubsubEvents.ingestStarted,
                authToken,
                { studies: [study] },
                customTagValue?.studyCondition || "",
                "",
                "dicom",
                humanSubjectId || ""
              );
            } catch (pubsubError) {
              console.error(
                `PubSub error for ingestStarted, study ${study.StudyInstanceUID}:`,
                pubsubError
              );
            }
          }

          const response = await uploadPortal(authToken, finalBlob, boundary);
          if (response?.ok) {
            dicomFilesSuccess = true;
            setUploadedFiles((prev) => ({ ...prev, dicom: true }));

            // Publish studyReady for each study
            for (const study of hierarchicalMetadata.studies) {
              try {
                await publishToPubSub(
                  pubsubEvents.studyReady,
                  authToken,
                  { studies: [study] },
                  customTagValue?.studyCondition || "",
                  "",
                  "dicom",
                  humanSubjectId || ""
                );
              } catch (pubsubError) {
                console.error(
                  `PubSub error for studyReady, study ${study.StudyInstanceUID}:`,
                  pubsubError
                );
              }
            }

            toast({
              variant: "default",
              title: "DICOM files uploaded successfully",
              duration: 30000,
            });
          } else {
            const error = `Upload failed: ${response?.status} ${response?.statusText}`;

            // Publish uploadFailed for each study
            for (const study of hierarchicalMetadata.studies) {
              try {
                await publishToPubSub(
                  pubsubEvents.uploadFailed,
                  authToken,
                  { studies: [study] },
                  customTagValue?.studyCondition || "",
                  error,
                  "dicom",
                  humanSubjectId || ""
                );
              } catch (pubsubError) {
                console.error(
                  `PubSub error for uploadFailed, study ${study.StudyInstanceUID}:`,
                  pubsubError
                );
              }
            }

            toast({
              variant: "destructive",
              title: error,
              duration: 30000,
            });
          }
        } catch (error: any) {
          console.error("DICOM upload error:", error);
        }
      }

      if (currentOtherFiles.length > 0 && !uploadedFiles.other) {
        otherFilesSuccess = true;
        const fileNamesForUpload = currentOtherFiles.map(file => 
        humanSubjectId ? `${humanSubjectId}_${file.name}` : file.name
      );
      
      const allFileNames = fileNamesForUpload.join(", ");
          await publishToPubSub(
            pubsubEvents.ingestStarted,
            authToken,
            "",
            "",
            "",
            `${allFileNames}`,
            humanSubjectId || ""
          );
        otherFilesSuccess = true;

        const failedFiles = [];
        const successfulFiles = [];

        for (let i = 0; i < currentOtherFiles.length; i++) {
        const file = currentOtherFiles[i];
        const prefixedFileName = fileNamesForUpload[i];
        
        // Pass the prefixed filename as the third parameter
        const success = await uploadSingleOtherFile(file, authToken, prefixedFileName);
        if (!success) {
            otherFilesSuccess = false;
            failedFiles.push(prefixedFileName);
          } else {
            successfulFiles.push(prefixedFileName);
          }
        }

        if (otherFilesSuccess) {
          setUploadedFiles((prev) => ({ ...prev, other: true }));
            await publishToPubSub(
              pubsubEvents.studyReady,
              authToken,
              "",
              "",
              "",
              `${successfulFiles.join(", ")}`,
              humanSubjectId || ""
            );
          toast({
            variant: "default",
            title: "All other files uploaded successfully",
            duration: 30000,
          });
        } else if (successfulFiles.length > 0 && failedFiles.length > 0) {
            await publishToPubSub(
              pubsubEvents.studyReady,
              authToken,
              "",
              "",
              "",
              ` ${successfulFiles.join(", ")}`
            );
            await publishToPubSub(
              pubsubEvents.uploadFailed,
              authToken,
              "",
              `Failed to upload some non-dicom files`,
              `${failedFiles.join(", ")}`
            );

          toast({
            variant: "destructive",
            title: `Partial upload completed. Failed: ${failedFiles.length}, Successful: ${successfulFiles.length}`,
            duration: 30000,
          });
        } else {
            await publishToPubSub(
              pubsubEvents.uploadFailed,
              authToken,
              "",
              "",
              `Failed to upload all non-DICOM files`,
              `${failedFiles.join(", ")}`,
              humanSubjectId || ""
            );
          
          toast({
            variant: "destructive",
            title: `All non-DICOM files failed to upload`,
            duration: 30000,
          });
        }
      }

      const hasOther = currentOtherFiles.length > 0;
      const hasDicom = currentDicomFiles.length > 0;

      if (
        (hasOther && !hasDicom && otherFilesSuccess) ||
        (!hasOther && hasDicom && dicomFilesSuccess) ||
        (hasOther && hasDicom && otherFilesSuccess && dicomFilesSuccess)
      ) {
        setTimeout(() => {
          setOpenAlert({
            open: true,
            alertTitle: "Success",
            alertDescription: "Files uploaded successfully",
          });
        }, 500);
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      setError(`Error uploading files: ${error}`);
      toast({
        variant: "destructive",
        title: `Error uploading files: ${error}`,
        duration: 30000,
      });
    } finally {
      setIsLoading(false);
      setSubmitButtonLoading(false);
    }
  };

  const handleDrop = async (e: any) => {
    e.preventDefault();
    setIsDragging(false);
    setError("");
    setSelectedFileName([]);
    setIsLoading(true);
    setHierarchicalMetadata(null);
    const files = e?.dataTransfer?.files || e?.target?.files;
    if (!files || files.length === 0) {
      setError("No files selected");
      setIsLoading(false);
      return;
    }
    const filesArray = Array.from(files) as File[];

    const dicomFiles = filesArray.filter((file) => {
      const lowerName = file.name.toLowerCase();
      return (
        lowerName.endsWith(".dcm") ||
        lowerName.endsWith(".dicom") ||
        lowerName.endsWith(".ima") ||
        !file.name.includes(".")
      );
    });

    const zipFiles = filesArray.filter((file) =>
      file.name.toLowerCase().endsWith(".zip")
    );
    const otherFiles = filesArray.filter((file) =>
      isSupportedOtherFile(file.name)
    );
    const unsupportedFiles = filesArray.filter(
      (file) =>
        !file.name.toLowerCase().endsWith(".dcm") &&
        !file.name.toLowerCase().endsWith(".dicom") &&
        !file.name.toLowerCase().endsWith(".ima") &&
        file.name.includes(".") &&
        !file.name.toLowerCase().endsWith(".zip") &&
        !isSupportedOtherFile(file.name)
    );

    if (unsupportedFiles.length > 0) {
      setError(
        `Unsupported file types: ${unsupportedFiles
          .map((f) => f.name)
          .join(", ")}`
      );
      setIsLoading(false);
      return;
    }

    try {
      let newDicomFiles: any[] = [];
      let newOtherFiles: File[] = [];

      // Process multiple ZIP files if present
      if (zipFiles.length > 0) {        
        // Process each ZIP file sequentially
        for (const zipFile of zipFiles) {
          try {
            setSelectedFileName((prevNames: any) => [...prevNames, zipFile.name]);
            
            const { dicomFiles: zipDicomFiles, otherFiles: zipOtherFiles } =
              await processZipFileWithSeparation(zipFile);
            
            newDicomFiles = [...newDicomFiles, ...zipDicomFiles];
            newOtherFiles = [...newOtherFiles, ...zipOtherFiles];
            
          } catch (zipError: any) {
            setError(`Error processing ZIP file ${zipFile.name}: ${zipError.message}`);
            setIsLoading(false);
            return;
          }
        }
      }

      // Process individual DICOM files
      if (dicomFiles.length > 0) {
        const dicomPromises = dicomFiles.map(async (file: File) => {
          setSelectedFileName((prevNames: any) => [...prevNames, file.name]);
          return await loadDicomFile(await file.arrayBuffer(), file.name);
        });
        const processedDicom = await Promise.all(dicomPromises);
        newDicomFiles = [...newDicomFiles, ...processedDicom];
      }

      // Add individual other files
      if (otherFiles.length > 0) {
        otherFiles.forEach((file) => {
          setSelectedFileName((prevNames: any) => [...prevNames, file.name]);
        });
        newOtherFiles = [...newOtherFiles, ...otherFiles];
      }
      const allDicomFiles = [...currentDicomFiles, ...newDicomFiles];
      const allOtherFiles = [...currentOtherFiles, ...newOtherFiles];
      // Set file type based on what we have
      if (allDicomFiles.length > 0 && allOtherFiles.length > 0) {
        setFileType("mixed");
      } else if (allDicomFiles.length > 0) {
        setFileType("dicom");
      } else if (allOtherFiles.length > 0) {
        setFileType("other");
      }
      // Process DICOM files if present
      if (allDicomFiles.length > 0) {
        setCurrentDicomFiles(allDicomFiles);
        const metadata = organizeMetadata(allDicomFiles);
        setHierarchicalMetadata(metadata);
        const validSeries = await handleToValidateQualifierPhantom(
          metadata,
          validatorJson
        );
        if (validSeries?.length === 0) {
          setError("No valid series found in the selected item.");
        }
        const data = handleGroupBySeries(validSeries, validatorJson);
        setSeriesDataToShow(data);
      }

      if (allOtherFiles.length > 0) {
        setCurrentOtherFiles(allOtherFiles);
      }
      setHasFiles(allDicomFiles.length > 0 || allOtherFiles.length > 0);
    } catch (error: any) {
      setError(`Error loading files: ${error.message}`);
      setSelectedFileName([]);
    } finally {
      setIsLoading(false);
    }
  };
  const createFileInput = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true; // Explicitly enable multiple file selection
    fileInput.accept = ".dcm,.dicom,.ima,.zip,.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.csv,.xls,.xlsx"; // Add accepted file types
    
    fileInput.addEventListener("change", (e: any) => {
      if (e?.target && e?.target?.files && e.target.files.length > 0) {
        handleDrop(e);
      }
    });
    
    return fileInput;
  };

  const handleStratagenId = (value: string) => {
    const selectedStudy = studyData?.find(
      (data: SecondaryStudySelection) => data.id === value
    );
    setStudyFields(selectedStudy || null);
    if (selectedStudy) {
      setCustomTagData({
        [selectedStudy.id]: {
          studyCondition: selectedStudy.studyCondition,
        },
      });
    } else {
      setCustomTagData({});
    }
  };

  const handleSelect = (
    item: string,
    studyFields: StudyFields | null,
    details: SecondaryStudyData
  ) => {
    if (!studyFields) {
      return;
    }
    setCustomTagData((prev: any) => {
      const existingData = prev[studyFields.id];
      const newData = {
        [studyFields.id]: {
          ...existingData,
          studyCondition: studyFields.studyCondition,
          [details.id]: item,
        },
      };
      return newData;
    });
  };

  const handleClearButton = () => {
    setOpenAlert(null);
    setIsLoading(false);
    setSelectedFileName([]);
    setCurrentDicomFiles([]);
    setCurrentOtherFiles([]);
    setHierarchicalMetadata(null);
    setSeriesDataToShow([]);
    setError("");
    setCustomTagData({});
    setStudyFields(null);
    setFileType(null);
    setHasFiles(false);
    setUploadedFiles({ dicom: false, other: false });
  };

  const handleClearDicomFiles = () => {
    setCurrentDicomFiles([]);
    setHierarchicalMetadata(null);
    setSeriesDataToShow([]);
    setCustomTagData({});
    setStudyFields(null);
    setUploadedFiles((prev) => ({ ...prev, dicom: false }));
    const otherFileNames = currentOtherFiles.map((file) => file.name);
    setSelectedFileName(otherFileNames);
    if (currentOtherFiles.length > 0) {
      setFileType("other");
    } else {
      setFileType(null);
      setHasFiles(false);
    }
  };

  const handleClearOtherFiles = () => {
    setCurrentOtherFiles([]);
    setUploadedFiles((prev) => ({ ...prev, other: false }));
    const dicomFileNames = currentDicomFiles.map(
      (file) => file.name || "DICOM file"
    );
    setSelectedFileName(dicomFileNames);
    if (currentDicomFiles.length > 0) {
      setFileType("dicom");
    } else {
      setFileType(null);
      setHasFiles(false);
    }
  };

  const shouldShowValidation = fileType === "dicom" || fileType === "mixed";
  const shouldShowOtherFiles = fileType === "other" || fileType === "mixed";

  return (
    <div className="h-screen pl-10 pr-10 pt-4">
      <div className="flex w-full justify-between h-[screen-16]">
        <div className="text-4xl flex gap-2 h-full">
          <img src={Logo} alt="Logo" className="w-16 h-16 mt-1" />
          <div className="flex flex-col font-medium">
            <span className="text-[#201D46]">Stratagen bio</span>
            <div className="font-medium text-3xl">Exam Upload</div>
          </div>
        </div>
        <div>
          <Button className="rounded bg-[#1e1b3c]" onClick={() => logout()}>
            Log out
          </Button>
        </div>
      </div>
      <div className="mt-4 text-4xl font-medium border-b-2 border-black">
        Exam Information
      </div>
      <div className="flex mt-4 gap-10 h-[screen-24] justify-between">
        <div className="w-full">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))] w-full gap-10 mb-4">
            <div>
              <TruncateWithTooltip
                text="Stratagen Study Type"
                className="block mb-1 font-medium"
              />
              <Select
                onValueChange={(value: string) => handleStratagenId(value)}
                value={!studyFields ? "" : studyFields?.id}
              >
                <SelectTrigger className={!studyFields ? "w-[50%]" : ""}>
                  <SelectValue placeholder="Stratagen Study Type" />
                </SelectTrigger>
                <SelectContent>
                  {studyData?.map((study: SecondaryStudySelection) => (
                    <SelectItem
                      value={study?.id}
                      className="block truncate"
                      key={study?.id}
                      title={study?.studyCondition}
                    >
                      {study?.studyCondition}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <StudyFieldsComponent
              studyFields={studyFields}
              handleSelect={handleSelect}
            />
          </div>
          <StudyFieldsComponent
            studyFields={studyFields}
            handleSelect={handleSelect}
            renderTextAreas={true}
          />
        </div>
      </div>
      {hasFiles && (
        <div className="flex flex-col w-full mt-4">
          {(shouldShowValidation || shouldShowOtherFiles) && (
            <div className="flex justify-end mt-4">
              <Button
                className="bg-[#ED230D] hover:bg-[#ED230D] rounded px-8"
                onClick={handleSubmit}
                disabled={
                  submitButtonLoading ||
                  (shouldShowValidation &&
                    !hierarchicalMetadata?.studies?.length) ||
                  (shouldShowOtherFiles &&
                    !shouldShowValidation &&
                    currentOtherFiles.length === 0) ||
                  (uploadedFiles.other &&
                    uploadedFiles.dicom &&
                    shouldShowValidation &&
                    !hierarchicalMetadata?.studies?.length)
                }
              >
                {submitButtonLoading && (
                  <Loader2 className="animate-spin mr-2" />
                )}
                {submitButtonLoading ? "Uploading..." : "Submit"}
              </Button>
            </div>
          )}
        </div>
      )}
      <div className="flex mt-2 gap-12 h-[screen-16] mt-4">
        <div
          className={`flex flex-col gap-3 ${
            hasFiles ? "w-1/5" : "w-full mt-4"
          }`}
        >
          <Button
            disabled={submitButtonLoading}
            className="rounded-xl bg-[#1e1b3c]"
            onClick={() => {
              const fileInput = createFileInput();
              fileInput.click();
            }}
          >
            Add Files
          </Button>
          <div
            onDrop={submitButtonLoading ? undefined : handleDrop}
            onDragOver={
              submitButtonLoading
                ? undefined
                : (e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }
            }
            onDragLeave={
              submitButtonLoading
                ? undefined
                : (e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }
            }
            onClick={
              submitButtonLoading
                ? undefined
                : () => {
                    const fileInput = createFileInput();
                    fileInput.click();
                  }
            }
            className={`flex flex-col justify-center items-center relative border-2 border-dashed rounded-xl p-4 mb-4 transition-colors min-h-[30vh] max-h-auto ${
              isDragging && !submitButtonLoading
                ? "border-blue-500 bg-blue-50"
                : "border-gray-900"
            } ${
              submitButtonLoading
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            }`}
          >
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
              </div>
            )}
            <div className="text-center mt-2 text-gray-900 text-2xl">
              {getDropAreaMessage(studyFields)}
            </div>
            {selectedFileName.length > 0 &&
              selectedFileName?.map((fileName: string, index: number) => {
                return (
                  <div
                    className="mt-3 text-sm text-ellipsis whitespace-nowrap w-full overflow-hidden text-center flex-col"
                    title={fileName}
                    key={index}
                  >
                    {fileName}
                  </div>
                );
              })}
          </div>
        </div>
        {hasFiles && (
          <div className="flex flex-col w-full overflow-x-auto">
            {shouldShowValidation && (
              <StudyInfoTable
                hierarchicalMetadata={hierarchicalMetadata}
                handleClearButton={handleClearDicomFiles}
                seriesDataToShow={seriesDataToShow}
                openGroupedSeriesRow={openGroupedSeriesRow}
                toggleRow={toggleRow}
                openRows={openRows}
                validatorJson={validatorJson}
                handleOpenRow={handleOpenRow}
                disabled={submitButtonLoading}
              />
            )}
            {shouldShowOtherFiles && (
              <div className="w-full mb-2 overflow-x-auto">
                <div className=" bg-gray-100 p-4 rounded-lg">
                  <div className="flex w-full justify-between">
                    <h3 className="text-lg font-medium mb-2">
                      Files Ready for Upload
                    </h3>
                    <Button
                      className="rounded bg-[#1e1b3c] w-[100px]"
                      onClick={handleClearOtherFiles}
                      disabled={submitButtonLoading}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="space-y-2 mt-2">
                    {currentOtherFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white rounded border w-full"
                      >
                        <div className="flex items-center gap-2 min-w-[80%] max-w-[100%]">
                          {uploadedFiles.other && (
                            <CheckCircle className="text-green-600" size={24} />
                          )}
                          <TruncateWithTooltip text={file.name} />
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Toaster />
      {openAlert && (
        <SuccessPopup
          openAlert={openAlert}
          alertTitle={openAlert?.alertTitle}
          alertDescription={openAlert?.alertDescription}
          handleClearButton={handleClearButton}
          logout={logout}
        />
      )}
    </div>
  );
}

export default UploadPortal;
